import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import Swal from 'sweetalert2';

interface FormData {
  name: string;
  description: string;
  businessType: string;
  personality: string;
  primaryGoal: string;
  website: string;
  knowledgeUrls: string;
}

const steps = [
  { label: 'Details', icon: SparklesIcon },
  { label: 'Personality', icon: ChatBubbleLeftRightIcon },
  { label: 'Knowledge', icon: DocumentTextIcon },
  { label: 'Review', icon: CheckIcon },
];

const CreateAssistant: React.FC = () => {
  const { assistantId } = useParams<{ assistantId: string }>();
  const navigate = useNavigate();
  const isEdit = !!assistantId;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [form, setForm] = useState<FormData>({
    name: '',
    description: '',
    businessType: '',
    personality: 'professional',
    primaryGoal: 'customer_support',
    website: '',
    knowledgeUrls: '',
  });

  useEffect(() => {
    if (isEdit) loadAssistant();
  }, [assistantId]);

  const loadAssistant = async () => {
    try {
      const res = await api.get(`/assistants/${assistantId}`);
      const a = res.data.assistant;
      if (a) {
        setForm({
          name: a.name || '',
          description: a.description || '',
          businessType: a.businessType || '',
          personality: a.personality || 'professional',
          primaryGoal: a.primaryGoal || 'customer_support',
          website: a.website || '',
          knowledgeUrls: '',
        });
      }
    } catch (err) {
      console.error('Failed to load assistant:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const canProceed = () => {
    if (step === 0) return form.name.trim().length > 0 && form.description.trim().length > 0;
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        businessType: form.businessType,
        personality: form.personality,
        primaryGoal: form.primaryGoal,
        website: form.website,
      };

      let savedId = assistantId;

      if (isEdit) {
        await api.put(`/assistants/${assistantId}`, payload);
      } else {
        const res = await api.post('/assistants', payload);
        savedId = res.data.assistant?.id || res.data.id;
      }

      // Ingest URLs if provided
      if (form.knowledgeUrls.trim() && savedId) {
        setIngesting(true);
        const urls = form.knowledgeUrls.split('\n').map((u) => u.trim()).filter(Boolean);
        for (const url of urls) {
          try {
            await api.post(`/assistants/${savedId}/ingest/url`, { url });
          } catch (ingestErr) {
            console.warn('Ingest failed for:', url, ingestErr);
          }
        }
        setIngesting(false);
      }

      Swal.fire({
        icon: 'success',
        title: isEdit ? 'Updated!' : 'Created!',
        text: `Assistant "${form.name}" has been ${isEdit ? 'updated' : 'created'} successfully.`,
        confirmButtonColor: '#00A4EE',
      }).then(() => navigate('/portal/assistants'));
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save assistant.';
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    } finally {
      setSaving(false);
      setIngesting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-picton-blue focus:border-picton-blue transition-all text-sm';

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Back */}
      <Link
        to="/portal/assistants"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Assistants
      </Link>

      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Assistant' : 'Create Assistant'}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {isEdit
            ? 'Update your assistant&apos;s details and knowledge base'
            : 'Set up a new AI chatbot for your business in a few steps'}
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-1">
        {steps.map((s, i) => (
          <React.Fragment key={s.label}>
            <button
              onClick={() => i <= step && setStep(i)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                i === step
                  ? 'bg-picton-blue text-white'
                  : i < step
                  ? 'bg-picton-blue/10 text-picton-blue cursor-pointer'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              <s.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{s.label}</span>
            </button>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        {/* Step 0: Details */}
        {step === 0 && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Assistant Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Sales Bot, Support Agent"
                className={inputClass}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Description *</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="What does this assistant do? What business is it for?"
                rows={3}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Type</label>
              <input
                name="businessType"
                value={form.businessType}
                onChange={handleChange}
                placeholder="e.g. E-commerce, Restaurant, SaaS"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Website URL</label>
              <input
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder="https://yourwebsite.com"
                className={inputClass}
              />
            </div>
          </div>
        )}

        {/* Step 1: Personality */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Personality Style</label>
              <select name="personality" value={form.personality} onChange={handleChange} className={inputClass}>
                <option value="professional">Professional & Formal</option>
                <option value="friendly">Friendly & Casual</option>
                <option value="concise">Concise & Direct</option>
                <option value="enthusiastic">Enthusiastic & Energetic</option>
                <option value="empathetic">Empathetic & Supportive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Primary Goal</label>
              <select name="primaryGoal" value={form.primaryGoal} onChange={handleChange} className={inputClass}>
                <option value="customer_support">Customer Support</option>
                <option value="lead_generation">Lead Generation</option>
                <option value="sales">Sales & Conversions</option>
                <option value="information">Information & FAQ</option>
                <option value="booking">Bookings & Appointments</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 2: Knowledge */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Knowledge Source URLs
              </label>
              <p className="text-xs text-gray-400 mb-2">
                Enter one URL per line. The assistant will crawl and learn from these pages.
              </p>
              <textarea
                name="knowledgeUrls"
                value={form.knowledgeUrls}
                onChange={handleChange}
                placeholder={"https://yourwebsite.com\nhttps://yourwebsite.com/about\nhttps://yourwebsite.com/products"}
                rows={5}
                className={inputClass}
              />
            </div>
            <div className="bg-picton-blue/5 border border-picton-blue/20 rounded-lg p-4">
              <p className="text-xs text-picton-blue">
                <strong>Tip:</strong> Add your homepage, about page, and product/service pages for the best results.
                You can add more URLs later from the assistant's edit page.
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Review Your Assistant</h3>
            <div className="divide-y divide-slate-100">
              {[
                ['Name', form.name],
                ['Description', form.description],
                ['Business Type', form.businessType || '—'],
                ['Personality', form.personality],
                ['Primary Goal', form.primaryGoal.replace('_', ' ')],
                ['Website', form.website || '—'],
                ['Knowledge URLs', form.knowledgeUrls ? `${form.knowledgeUrls.split('\n').filter(Boolean).length} URL(s)` : 'None'],
              ].map(([label, value]) => (
                <div key={label} className="flex py-3">
                  <span className="text-sm font-medium text-gray-500 w-36 flex-shrink-0">{label}</span>
                  <span className="text-sm text-gray-900">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>

        {step < steps.length - 1 ? (
          <button
            onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
            disabled={!canProceed()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-picton-blue text-white text-sm font-semibold rounded-lg hover:bg-picton-blue/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Next
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-picton-blue text-white text-sm font-semibold rounded-lg hover:bg-picton-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {ingesting ? 'Ingesting…' : 'Saving…'}
              </>
            ) : (
              <>
                <CheckIcon className="h-4 w-4" />
                {isEdit ? 'Save Changes' : 'Create Assistant'}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateAssistant;
